import { createContext, useContext } from 'react'
import type { LessonCallSession, LessonCallType, WebrtcConfig } from '@/api/lessonCalls'
import type { VoiceLevelsListener } from './useVoiceActivity'

/**
 * Контекст звонка вынесен из CallProvider.tsx: файл с компонентом не должен
 * экспортировать ничего, кроме компонентов, иначе ломается fast refresh.
 */

export type CallStatus = 'idle' | 'incoming' | 'connecting' | 'active' | 'ending'

export type { VoiceActivityLevels } from './useVoiceActivity'

export type CallParticipantView = {
  userId: number
  name: string
  connectionState: RTCPeerConnectionState | 'local'
}

export type LessonCallStateEvent = {
  lesson_id: number
  session: LessonCallSession
}

export type CallContextValue = {
  status: CallStatus
  session: LessonCallSession | null
  config: WebrtcConfig | null
  participants: CallParticipantView[]
  remoteStreams: Record<number, MediaStream>
  localStream: MediaStream | null
  micEnabled: boolean
  cameraEnabled: boolean
  screenSharing: boolean
  /** Секунды с начала звонка. */
  duration: number
  error: string | null
  errorCode: string | null
  /** Индикатор качества связи: 0 — плохо, 1 — средне, 2 — хорошо. */
  quality: number | null
  /**
   * Подписка на уровни голоса (0..1 по user id). Минуя React: значения
   * меняются ~15 раз в секунду и пишутся подписчиком прямо в стиль узла.
   */
  subscribeVoiceLevels: (listener: VoiceLevelsListener) => () => void
  startCall: (type?: LessonCallType) => Promise<void>
  acceptCall: () => Promise<void>
  declineCall: () => void
  leaveCall: () => Promise<void>
  endCall: () => Promise<void>
  toggleMic: () => void
  toggleCamera: () => Promise<void>
  toggleScreenShare: () => Promise<void>
  clearError: () => void
}

export const CallContext = createContext<CallContextValue | null>(null)

export function useLessonCall(): CallContextValue {
  const context = useContext(CallContext)
  if (!context) {
    throw new Error('useLessonCall должен использоваться внутри <CallProvider>')
  }
  return context
}
